// Frequently asked questions about Jobily — collapsible accordion.

import { useState } from "react";
import { ChevronDown } from "lucide-react";

interface FaqItem {
  question: string;
  answer: string;
}

const FAQS: FaqItem[] = [
  {
    question: "What is Jobily?",
    answer:
      "Jobily is a Chrome extension that lives in your browser sidebar. It auto-fills job application forms, generates tailored cover letters using AI, and answers custom application questions — all based on your resume and profile data.",
  },
  {
    question: "How does auto-fill work?",
    answer:
      "Once you set up your profile (name, email, phone, address, etc.), click the Auto Fill button on any job application page. Jobily detects form fields and fills them with your saved data instantly.",
  },
  {
    question: "How are cover letters generated?",
    answer:
      "Paste a job description into the Cover Letter tab. Jobily's AI reads the job requirements and your resume data, then writes a tailored cover letter — no generic templates, no placeholders. It downloads as a PDF.",
  },
  {
    question: "What about custom application questions?",
    answer:
      "After pasting a job description, switch to the Q&A section. Type any question from the application form, and Jobily generates an answer that combines the job context with your actual experience and skills.",
  },
  {
    question: "Is my data safe?",
    answer:
      "Yes. Your profile data is encrypted and stored securely with Row Level Security — only you can access your data. We never sell your information. AI processing uses your data only to generate your content, nothing else.",
  },
  {
    question: "How much does Jobily cost?",
    answer:
      "You get 24 hours of full access with no account needed. After that, it's $10/month billed via Stripe. Cancel anytime — no contracts, no hidden fees.",
  },
  {
    question: "What is the Web Portal for?",
    answer:
      "The Web Portal gives you a full-screen workspace to manage your profile, including detailed fields like address, date of birth, work authorization, and more that don't fit in the browser sidebar. Everything syncs automatically with the extension.",
  },
  {
    question: "Can I use Jobily without the extension?",
    answer:
      "The auto-fill feature requires the Chrome extension since it needs to interact with the job application page. Cover letters and Q&A can be generated from the Web Portal as well.",
  },
  {
    question: "How do I cancel my subscription?",
    answer:
      "Go to Settings in the extension or Web Portal and click 'Manage Subscription'. This opens your Stripe billing portal where you can cancel anytime. You'll retain access until the end of your billing period.",
  },
];

export default function FaqTab() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-4 animate-fade-up">
      <div>
        <h3 className="font-display font-bold text-lg text-foreground">
          Frequently Asked Questions
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Everything you need to know about Jobily.
        </p>
      </div>

      <div className="space-y-2">
        {FAQS.map((faq, i) => {
          const isOpen = openIndex === i;
          return (
            <div
              key={i}
              className="rounded-xl border border-border bg-card overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : i)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/30 transition-colors"
              >
                <span className="text-sm font-medium text-foreground pr-4">
                  {faq.question}
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {isOpen && (
                <div className="px-4 pb-3">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
